import { FormattedMessage, useIntl } from "react-intl";
import { Tooltip } from "@/components/common/Tooltip";
import { Disclosure } from "@/components/design/Disclosure";
import { InfoTooltip } from "@/components/design/InfoTooltip";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import { Switch } from "@/components/design/Switch";
import type { MatchData } from "./match-design-types";

// Bewusst ohne candidateAssessmentPrompt: das Freitext-Feld ist vorerst aus der UI
// entfernt, weil sich der Zusatz nicht zuverlaessig auf die konfigurierbaren Dimensionen
// begrenzen laesst (Prompt-Injection-Kanal). Gespeicherte Werte round-trippen weiter
// ueber den Codec; genesis ignoriert sie serverseitig.
type PromptData = Pick<
  MatchData,
  | "aiEnabled"
  | "candidateAssessmentDetail"
  | "candidateAssessmentStrictness"
  | "candidateAssessmentOutcomeFocus"
  | "requirementLeniency"
>;

function SettingLabel({
  label,
  ariaLabel,
  descriptions,
}: {
  label: string;
  ariaLabel: string;
  descriptions: Array<{ label: string; description: string }>;
}) {
  return (
    <div className="mb-1.5 flex items-center gap-1 leading-none">
      <span className="field-label">{label}</span>
      <InfoTooltip
        ariaLabel={ariaLabel}
        title={label}
        contentClassName="w-[20rem] max-w-[calc(100vw-2rem)]"
      >
        <span className="block space-y-1.5">
          {descriptions.map((item) => (
            <span key={item.label} className="block">
              <span className="font-medium">{item.label}:</span> {item.description}
            </span>
          ))}
        </span>
      </InfoTooltip>
    </div>
  );
}

export function CandidateAssessmentPromptEditor({
  data,
  update,
  readOnly,
  showStyleRegulators = true,
}: {
  data: PromptData;
  update: (patch: Partial<MatchData>) => void;
  readOnly: boolean;
  // Ausführlichkeit / Bewertungsmaßstab / Ergebnisfokus steuern nur die
  // Kandidaten-Einschätzung, die genesis ausschließlich im RANK-Modus erzeugt
  // (MatchResultGenerateController, spark #233). In Assess/Optimize wären sie
  // wirkungslos, deshalb dort ausblenden – KI-Schalter + Anforderungsprüfung bleiben.
  showStyleRegulators?: boolean;
}) {
  const intl = useIntl();
  // Ein Schalter für die gesamte KI im Prüfen-Modus: dataToDesign schreibt ihn als
  // aiMode auf alle KI-fähigen Conditions, genesis leitet daraus Einzelprüfungen
  // UND Kandidaten-Einschätzung ab. Die Stil-Regler sind ohne KI wirkungslos,
  // deshalb dann disabled.
  const controlsDisabled = readOnly || !data.aiEnabled;

  return (
    // Peer-Karte zu Rollenanforderungen/Team Powers: dieselbe Disclosure (14px-Titel,
    // Chevron, einklappbar) statt des groesseren BoxHeader — die Groesse soll keine
    // Hierarchie behaupten, die es nicht gibt. Das KI-Signal traegt der stateful
    // smycles-ai-shadow ueber className, analog zu den Anforderungs-Karten (UI_CONVENTIONS,
    // Where AI lives); der KI-Schalter sitzt im headerAction-Slot.
    <Disclosure
      defaultOpen
      className={`transition ${data.aiEnabled ? "smycles-ai-shadow" : ""}`}
      title={
        <FormattedMessage
          id="match.challenge.hrPromptLabel"
          defaultMessage="Bewertungsstil"
        />
      }
      headerAction={
        <div className="flex items-center gap-2">
          <span className="field-label leading-none">
            <FormattedMessage
              id="match.challenge.aiAssist"
              defaultMessage="KI-Unterstützung"
            />
          </span>
          <Tooltip
            placement="left"
            content={
              // Die per-Person-Einschätzung mit Score erzeugt genesis nur im RANK-Modus
              // (MatchResultGenerateController); in Assess/Optimize prüft die KI nur die
              // nicht belegbaren Anforderungen (spark #233).
              showStyleRegulators ? (
              <FormattedMessage
                id="match.challenge.aiAssistHint"
                defaultMessage="KI berücksichtigt das ganze Profil und beurteilt, ob Anforderungen auch ohne expliziten Nachweis erfüllt sind. Zusätzlich erstellt sie zu jeder Person eine Einschätzung mit Score."
              />
              ) : (
              <FormattedMessage
                id="match.challenge.aiAssistHintReduced"
                defaultMessage="KI berücksichtigt das ganze Profil und beurteilt, ob Anforderungen auch ohne expliziten Nachweis erfüllt sind."
              />
              )
            }
          >
            <Switch
              checked={data.aiEnabled}
              onChange={(aiEnabled) => update({ aiEnabled })}
              disabled={readOnly}
              ariaLabel={intl.formatMessage({
                id: "match.challenge.aiAssist",
                defaultMessage: "KI-Unterstützung",
              })}
            />
          </Tooltip>
        </div>
      }
    >
      {/* Flex-Wrap statt Grid: die Optionen der Regler sind einzeilig (nowrap),
          also muss der ganze Block umbrechen duerfen, wenn die halbe Zeile nicht
          reicht. min-width:auto (Flex-Default) verhindert das Ueberlappen, das
          minmax(0,1fr)-Grid zuliess. */}
      {/* Ohne KI finden die Stil-Regler keine Anwendung (genesis liest sie nur im
          AI-Pfad): Blur + Opacity folgt der Blur-Relevanz-Sprache (Team Powers,
          Coming-Soon), inert nimmt Regler UND Info-Tooltips aus Tab-Reihenfolge
          und Interaktion, analog zum zugeklappten Disclosure-Body. */}
      <div
        className={`flex flex-wrap gap-4 transition ${
          data.aiEnabled ? "" : "opacity-60 blur-[2px]"
        }`}
        inert={!data.aiEnabled}
        data-testid="assessment-style-settings"
      >
        {showStyleRegulators && (
        <>
        <div className="grow basis-[calc(50%-0.5rem)]">
          <SettingLabel
            label={intl.formatMessage({
              id: "match.challenge.assessmentDetail",
              defaultMessage: "Ausführlichkeit",
            })}
            ariaLabel={intl.formatMessage({
              id: "match.challenge.assessmentDetailInfo",
              defaultMessage: "Ausführlichkeit erklären",
            })}
            descriptions={[
              {
                label: intl.formatMessage({
                  id: "match.challenge.assessmentDetailBullets",
                  defaultMessage: "Stichpunkte",
                }),
                description: intl.formatMessage({
                  id: "match.challenge.assessmentDetailBulletsDescription",
                  defaultMessage: "Kompakte Punkte mit Zeilenumbrüchen.",
                }),
              },
              {
                label: intl.formatMessage({
                  id: "match.challenge.assessmentDetailSentences",
                  defaultMessage: "Sätze",
                }),
                description: intl.formatMessage({
                  id: "match.challenge.assessmentDetailSentencesDescription",
                  defaultMessage: "Kurze, vollständige Sätze.",
                }),
              },
              {
                label: intl.formatMessage({
                  id: "match.challenge.assessmentDetailLongText",
                  defaultMessage: "Langtext",
                }),
                description: intl.formatMessage({
                  id: "match.challenge.assessmentDetailLongTextDescription",
                  defaultMessage: "Ausführliche Begründung mit Belegen.",
                }),
              },
            ]}
          />
          <SegmentedControl
            value={data.candidateAssessmentDetail}
            onChange={(candidateAssessmentDetail) => update({ candidateAssessmentDetail })}
            size="md"
            options={[
              {
                value: "BULLETS",
                disabled: controlsDisabled,
                label: intl.formatMessage({
                  id: "match.challenge.assessmentDetailBullets",
                  defaultMessage: "Stichpunkte",
                }),
              },
              {
                value: "SENTENCES",
                disabled: controlsDisabled,
                label: intl.formatMessage({
                  id: "match.challenge.assessmentDetailSentences",
                  defaultMessage: "Sätze",
                }),
              },
              {
                value: "LONG_TEXT",
                disabled: controlsDisabled,
                label: intl.formatMessage({
                  id: "match.challenge.assessmentDetailLongText",
                  defaultMessage: "Langtext",
                }),
              },
            ]}
            aria-label={intl.formatMessage({
              id: "match.challenge.assessmentDetail",
              defaultMessage: "Ausführlichkeit",
            })}
          />
        </div>
        <div className="grow basis-[calc(50%-0.5rem)]">
          <SettingLabel
            label={intl.formatMessage({
              id: "match.challenge.assessmentStrictness",
              defaultMessage: "Bewertungsmaßstab",
            })}
            ariaLabel={intl.formatMessage({
              id: "match.challenge.assessmentStrictnessInfo",
              defaultMessage: "Bewertungsmaßstab erklären",
            })}
            descriptions={[
              {
                label: intl.formatMessage({
                  id: "match.challenge.assessmentStrictnessNormal",
                  defaultMessage: "Normal",
                }),
                description: intl.formatMessage({
                  id: "match.challenge.assessmentStrictnessNormalDescription",
                  defaultMessage: "Verwendet die normale Bewertungskalibrierung.",
                }),
              },
              {
                label: intl.formatMessage({
                  id: "match.challenge.assessmentStrictnessStrict",
                  defaultMessage: "Hoch",
                }),
                description: intl.formatMessage({
                  id: "match.challenge.assessmentStrictnessStrictDescription",
                  defaultMessage: "Unklare oder nur angedeutete Erfahrung senkt den Score moderat.",
                }),
              },
              {
                label: intl.formatMessage({
                  id: "match.challenge.assessmentStrictnessExtreme",
                  defaultMessage: "Sehr hoch",
                }),
                description: intl.formatMessage({
                  id: "match.challenge.assessmentStrictnessExtremeDescription",
                  defaultMessage: "Nur explizite, rollenrelevante Belege zählen; jede Lücke kostet deutlich Punkte.",
                }),
              },
            ]}
          />
          <SegmentedControl
            value={data.candidateAssessmentStrictness}
            onChange={(candidateAssessmentStrictness) =>
              update({ candidateAssessmentStrictness })}
            size="md"
            options={[
              {
                value: "NORMAL",
                disabled: controlsDisabled,
                label: intl.formatMessage({
                  id: "match.challenge.assessmentStrictnessNormal",
                  defaultMessage: "Normal",
                }),
              },
              {
                value: "STRICT",
                disabled: controlsDisabled,
                label: intl.formatMessage({
                  id: "match.challenge.assessmentStrictnessStrict",
                  defaultMessage: "Hoch",
                }),
              },
              {
                value: "EXTREME",
                disabled: controlsDisabled,
                label: intl.formatMessage({
                  id: "match.challenge.assessmentStrictnessExtreme",
                  defaultMessage: "Sehr hoch",
                }),
              },
            ]}
            aria-label={intl.formatMessage({
              id: "match.challenge.assessmentStrictness",
              defaultMessage: "Bewertungsmaßstab",
            })}
          />
        </div>
        </>
        )}
        <div className="grow basis-[calc(50%-0.5rem)]">
          <SettingLabel
            label={intl.formatMessage({
              id: "match.challenge.requirementLeniency",
              defaultMessage: "Anforderungsprüfung",
            })}
            ariaLabel={intl.formatMessage({
              id: "match.challenge.requirementLeniencyInfo",
              defaultMessage: "Anforderungsprüfung erklären",
            })}
            descriptions={[
              {
                label: intl.formatMessage({
                  id: "match.challenge.requirementLeniencyLenient",
                  defaultMessage: "Kulant",
                }),
                description: intl.formatMessage({
                  id: "match.challenge.requirementLeniencyLenientDescription",
                  defaultMessage:
                    "Die geforderte Stufe ist ein Richtwert: vorhandenes Fachwissen genügt.",
                }),
              },
              {
                label: intl.formatMessage({
                  id: "match.challenge.requirementLeniencyMustStrict",
                  defaultMessage: "Kulant bei Kann",
                }),
                description: intl.formatMessage({
                  id: "match.challenge.requirementLeniencyMustStrictDescription",
                  defaultMessage:
                    "Bei Muss-Kriterien muss die Stufe erreicht sein, bei Kann genügt Vorhandensein.",
                }),
              },
              {
                label: intl.formatMessage({
                  id: "match.challenge.requirementLeniencyStrict",
                  defaultMessage: "Beides streng",
                }),
                description: intl.formatMessage({
                  id: "match.challenge.requirementLeniencyStrictDescription",
                  defaultMessage:
                    "Die geforderte Stufe muss bei Muss und Kann erreicht sein.",
                }),
              },
            ]}
          />
          <SegmentedControl
            value={data.requirementLeniency}
            onChange={(requirementLeniency) => update({ requirementLeniency })}
            size="md"
            optionClassName="whitespace-nowrap"
            options={[
              {
                value: "LENIENT",
                disabled: controlsDisabled,
                label: intl.formatMessage({
                  id: "match.challenge.requirementLeniencyLenient",
                  defaultMessage: "Kulant",
                }),
              },
              {
                value: "MUST_STRICT",
                disabled: controlsDisabled,
                label: intl.formatMessage({
                  id: "match.challenge.requirementLeniencyMustStrict",
                  defaultMessage: "Kulant bei Kann",
                }),
              },
              {
                value: "STRICT",
                disabled: controlsDisabled,
                label: intl.formatMessage({
                  id: "match.challenge.requirementLeniencyStrict",
                  defaultMessage: "Beides streng",
                }),
              },
            ]}
            aria-label={intl.formatMessage({
              id: "match.challenge.requirementLeniency",
              defaultMessage: "Anforderungsprüfung",
            })}
          />
        </div>
        {showStyleRegulators && (
        <div className="grow basis-[calc(50%-0.5rem)]">
          <SettingLabel
            label={intl.formatMessage({
              id: "match.challenge.assessmentOutcomeFocus",
              defaultMessage: "Ergebnisfokus",
            })}
            ariaLabel={intl.formatMessage({
              id: "match.challenge.assessmentOutcomeFocusInfo",
              defaultMessage: "Ergebnisfokus erklären",
            })}
            descriptions={[
              {
                label: intl.formatMessage({
                  id: "match.challenge.assessmentOutcomeRisks",
                  defaultMessage: "Lücken",
                }),
                description: intl.formatMessage({
                  id: "match.challenge.assessmentOutcomeRisksDescription",
                  defaultMessage: "Prüft den Lebenslauf gezielt auf Lücken und benennt sie zuerst.",
                }),
              },
              {
                label: intl.formatMessage({
                  id: "match.challenge.assessmentOutcomeBalanced",
                  defaultMessage: "Ausgewogen",
                }),
                description: intl.formatMessage({
                  id: "match.challenge.assessmentOutcomeBalancedDescription",
                  defaultMessage: "Bewertet neutral, Stärken und Lücken gleichgewichtet.",
                }),
              },
              {
                label: intl.formatMessage({
                  id: "match.challenge.assessmentOutcomeOpportunities",
                  defaultMessage: "Stärken",
                }),
                description: intl.formatMessage({
                  id: "match.challenge.assessmentOutcomeOpportunitiesDescription",
                  defaultMessage: "Bewertet wohlwollend: Stärken und Einsatzmöglichkeiten zuerst.",
                }),
              },
            ]}
          />
          <SegmentedControl
            value={data.candidateAssessmentOutcomeFocus}
            onChange={(candidateAssessmentOutcomeFocus) =>
              update({ candidateAssessmentOutcomeFocus })}
            size="md"
            options={[
              {
                value: "RISKS",
                disabled: controlsDisabled,
                label: intl.formatMessage({
                  id: "match.challenge.assessmentOutcomeRisks",
                  defaultMessage: "Lücken",
                }),
              },
              {
                value: "BALANCED",
                disabled: controlsDisabled,
                label: intl.formatMessage({
                  id: "match.challenge.assessmentOutcomeBalanced",
                  defaultMessage: "Ausgewogen",
                }),
              },
              {
                value: "OPPORTUNITIES",
                disabled: controlsDisabled,
                label: intl.formatMessage({
                  id: "match.challenge.assessmentOutcomeOpportunities",
                  defaultMessage: "Stärken",
                }),
              },
            ]}
            aria-label={intl.formatMessage({
              id: "match.challenge.assessmentOutcomeFocus",
              defaultMessage: "Ergebnisfokus",
            })}
          />
        </div>
        )}
      </div>
    </Disclosure>
  );
}
